import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";

const POLL_INTERVAL_MS = 30_000;

function formatTimestamp(value) {
  if (!value) return "Not recorded";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString("en-GB");
}

function formatHeartbeatAge(value) {
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds < 0) return "Unknown";
  if (seconds < 60) return `${Math.round(seconds)} seconds`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} minutes`;
  return `${(seconds / 3600).toFixed(1)} hours`;
}

export default function OperatorExecutionLeaseCard({
  operator,
  onStatusChange,
  refreshToken = 0,
}) {
  const operatorKey = operator?.key;
  const [leaseStatus, setLeaseStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [reason, setReason] = useState("");
  const [confirmationRunId, setConfirmationRunId] = useState("");
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (!operatorKey) return undefined;
    let cancelled = false;
    const load = async ({ quiet = false } = {}) => {
      if (!quiet) setLoading(true);
      try {
        const { data } = await api.get(
          `/admin/operator-automation/execution-lease/${operatorKey}`,
        );
        if (!cancelled) {
          setLeaseStatus(data);
          onStatusChange?.(data);
        }
      } catch (error) {
        if (!cancelled && !quiet) {
          toast.error(
            "Execution lease status failed: "
              + (error.response?.data?.detail?.error
                || error.response?.data?.detail
                || error.message),
          );
        }
      } finally {
        if (!cancelled && !quiet) setLoading(false);
      }
    };
    setLeaseStatus(null);
    onStatusChange?.(null);
    setReason("");
    setConfirmationRunId("");
    setConfirmed(false);
    load();
    const timer = setInterval(() => load({ quiet: true }), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [onStatusChange, operatorKey, refreshToken]);

  if (!operator) return null;

  const active = leaseStatus?.active === true;
  const recoveryEligible = active && leaseStatus?.recovery_eligible === true;
  const currentRunId = String(leaseStatus?.run_id || "");
  const canRecover = (
    recoveryEligible
    && confirmed
    && reason.trim().length >= 10
    && confirmationRunId.trim() === currentRunId
    && !recovering
  );

  const recover = async () => {
    if (!canRecover) return;
    setRecovering(true);
    try {
      const { data } = await api.post(
        `/admin/operator-automation/execution-lease/${operatorKey}/recover`,
        {
          expected_run_id: confirmationRunId.trim(),
          reason: reason.trim(),
          confirm_stale_recovery: true,
        },
      );
      setLeaseStatus(data.lease_status);
      onStatusChange?.(data.lease_status);
      setReason("");
      setConfirmationRunId("");
      setConfirmed(false);
      toast.success(
        "Abandoned execution lease recovered. No competition data or automation configuration was changed.",
      );
    } catch (error) {
      toast.error(
        "Execution lease recovery refused: "
          + (error.response?.data?.detail?.error
            || error.response?.data?.detail
            || error.message),
      );
    } finally {
      setRecovering(false);
    }
  };

  const previousRecovery = leaseStatus?.previous_recovery_audit;

  return (
    <section
      className="rounded-lg border border-white/10 bg-white/[0.02] p-4 space-y-3"
      data-testid="operator-execution-lease-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm font-semibold text-white">Execution lease</div>
          <div className="text-[11px] text-white/55 mt-1">
            Operator-wide mutual exclusion for manual and scheduled automation.
          </div>
        </div>
        <span
          className="px-2 py-0.5 rounded border border-white/10 bg-white/[0.04] text-[10px] uppercase text-white/65"
          data-testid="operator-execution-lease-classification"
        >
          {loading ? "Loading" : leaseStatus?.classification || "Unknown"}
        </span>
      </div>

      {leaseStatus && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2 text-[11px]" data-testid="operator-execution-lease-details">
          <LeaseField label="Active" value={active ? "Yes" : "No"} />
          <LeaseField label="Execution type" value={leaseStatus.execution_type || "None"} />
          <LeaseField label="Owner" value={leaseStatus.owner || "None"} />
          <LeaseField label="Run ID" value={leaseStatus.run_id || "None"} />
          <LeaseField label="Acquired" value={formatTimestamp(leaseStatus.acquired_at)} />
          <LeaseField label="Latest heartbeat" value={formatTimestamp(leaseStatus.heartbeat_at)} />
          <LeaseField label="Heartbeat age" value={formatHeartbeatAge(leaseStatus.heartbeat_age_seconds)} />
          <LeaseField label="Expiry" value={formatTimestamp(leaseStatus.expires_at)} />
          <LeaseField label="Schedule slot" value={leaseStatus.schedule_slot || "Manual / none"} />
          <LeaseField label="Recovery eligible" value={leaseStatus.recovery_eligible ? "Yes" : "No"} />
          <LeaseField label="Recovery reason" value={leaseStatus.recovery_reason || "None"} />
          <LeaseField
            label="Stale threshold"
            value={formatHeartbeatAge(leaseStatus.heartbeat_stale_threshold_seconds)}
          />
        </div>
      )}

      {active && !recoveryEligible && (
        <div className="rounded border border-emerald-500/20 bg-emerald-500/10 px-3 py-2 text-[11px] text-emerald-100">
          This lease is protected. Owner changes or deployment changes alone do not permit recovery.
        </div>
      )}

      {recoveryEligible && (
        <div className="rounded border border-amber-500/30 bg-amber-500/10 p-3 space-y-3" data-testid="operator-execution-lease-recovery-controls">
          <div className="text-xs font-semibold text-amber-100">
            Exceptional stale-lease recovery
          </div>
          <p className="text-[11px] text-amber-100/80">
            Recovery rechecks heartbeat staleness atomically. It does not run automation or alter competition data or automation configuration.
          </p>
          <label className="block space-y-1 text-[11px] text-white/70">
            <span>Administrative reason</span>
            <textarea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              rows={2}
              className="w-full rounded border border-white/10 bg-black/20 px-2 py-1.5 text-white"
              data-testid="operator-execution-lease-recovery-reason"
            />
          </label>
          <label className="block space-y-1 text-[11px] text-white/70">
            <span>Type the exact run ID to confirm</span>
            <input
              value={confirmationRunId}
              onChange={(event) => setConfirmationRunId(event.target.value)}
              className="w-full rounded border border-white/10 bg-black/20 px-2 py-1.5 font-mono text-white"
              data-testid="operator-execution-lease-recovery-run-id"
            />
          </label>
          <label className="flex items-start gap-2 text-[11px] text-white/70">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(event) => setConfirmed(event.target.checked)}
              data-testid="operator-execution-lease-recovery-confirm"
            />
            I confirm this exact stale run should be marked abandoned and recovered.
          </label>
          <button
            type="button"
            onClick={recover}
            disabled={!canRecover}
            className="px-3 py-2 rounded text-xs border border-amber-400/30 bg-amber-500/20 text-amber-100 disabled:opacity-45"
            data-testid="operator-execution-lease-recover-btn"
          >
            {recovering ? "Recovering..." : "Recover this stale lease"}
          </button>
        </div>
      )}

      {previousRecovery && (
        <div className="rounded border border-white/10 bg-black/10 px-3 py-2 text-[11px] text-white/65" data-testid="operator-execution-lease-previous-recovery">
          Previous recovery: {previousRecovery.previous_run_id || "unknown run"} at {formatTimestamp(previousRecovery.recovered_at)} ({previousRecovery.recovery_reason || "reason unavailable"}).
        </div>
      )}
    </section>
  );
}

function LeaseField({ label, value }) {
  return (
    <div className="rounded border border-white/10 bg-black/10 px-2 py-1.5 min-w-0">
      <div className="uppercase text-[9px] text-white/35">{label}</div>
      <div className="mt-0.5 text-white/75 break-all">{value}</div>
    </div>
  );
}
